

// Google Maps types declaration
declare global {
  interface Window {
    google: any;
  }
}

import { zodResolver } from '@hookform/resolvers/zod';
import { Alert } from '@repo/ui/components/ui/alert';
import { Button } from '@repo/ui/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/components/ui/form';
import { Input } from '@repo/ui/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/components/ui/select';
import { MapCoordinates } from '@repo/ui/features/inventaires/map-view/providers';
import LeafletMap from '@repo/ui/components/leaflet-map';
import { useSaveEpaysageBase } from '@repo/ui/hooks/api/epaysages';
import { EpaysageBaseSaveInput, InventaireResponse } from '@repo/types';
import { AlertCircle, Hash, MapPin, Ruler, TreePine } from 'lucide-react';
import { toast } from 'sonner';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const DEFAULT_CENTER: MapCoordinates = { lat: 46.603354, lng: 1.888334 };

const TYPE_OPTIONS = [
  { value: 'espace-boise', label: 'Espace boisé' },
  { value: 'espace-boise-classe', label: 'Espace boisé classé' },
  { value: 'espace-paysage-protege', label: 'Espace Paysage protégé' },
  { value: 'zone-naturelle', label: 'Zone naturelle' },
  { value: 'autre', label: 'Autre (à préciser)' },
];

const coordinateSchema = z.object({
  lat: z.number(),
  lng: z.number(),
});

const baseFormSchema = z
  .object({
    address: z.string().min(1, "L'adresse est requise"),
    ville: z.string().min(1, 'La ville est requise'),
    pays: z.string().min(1, 'Le pays est requis'),
    zip_code: z.string().optional(),
    area: z
      .string()
      .optional()
      .refine((value) => {
        if (!value) return true;
        const parsed = Number(value);
        return !Number.isNaN(parsed) && parsed > 0;
      }, 'La superficie doit être un nombre positif'),
    type_espace: z.string().min(1, "Le type d'espace est requis"),
    type_espace_autre: z.string().optional(),
    code_site: z.string().optional(),
    num_sujet: z.string().optional(),
    age_estime: z
      .string()
      .optional()
      .refine((value) => {
        if (!value) return true;
        const parsed = Number(value);
        return !Number.isNaN(parsed) && parsed >= 0;
      }, "L'âge estimé doit être un nombre positif ou zéro"),
    polygon: z.array(coordinateSchema).min(3, 'Définissez au moins trois points sur la carte'),
  })
  .superRefine((values, ctx) => {
    if (values.type_espace === 'autre' && !values.type_espace_autre?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['type_espace_autre'],
        message: 'Précisez le type lorsque vous choisissez "Autre"',
      });
    }
  });

export type EpaysageBasicFormValues = z.infer<typeof baseFormSchema>;
export type EpaysageBasicFormInitialValues = Partial<Omit<EpaysageBasicFormValues, 'polygon'>> & { zip_code?: string };
const formSchema: z.ZodType<EpaysageBasicFormValues> = baseFormSchema;

interface EpaysageBasicFormProps {
  mode?: 'CREATE' | 'UPDATE';
  epaysageId?: number;
  inventaireId?: number;
  initialValues?: EpaysageBasicFormInitialValues;
  initialPolygon?: MapCoordinates[];
  onCancel?: () => void;
  onSuccess?: (params: { inventaireId: number; epaysageId: number }) => void;
  isFinished?: boolean;
}

export function EpaysageBasicForm({
  mode = 'CREATE',
  epaysageId,
  inventaireId,
  initialValues,
  initialPolygon,
  onCancel,
  onSuccess,
  isFinished = true,
}: EpaysageBasicFormProps) {
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [mapZoom, setMapZoom] = useState<number>(initialPolygon && initialPolygon.length > 0 ? 14 : 14);
  const [selectedAddressCenter, setSelectedAddressCenter] = useState<MapCoordinates | null>(null);

  const defaultValues = useMemo(
    () => buildDefaultValues(initialValues, initialPolygon),
    [initialValues, initialPolygon]
  );

  const form = useForm<EpaysageBasicFormValues>({
    resolver: zodResolver(formSchema as any),
    defaultValues,
  });

  useEffect(() => {
    form.reset(buildDefaultValues(initialValues, initialPolygon));
  }, [form, initialValues, initialPolygon]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const addressInputContainer = target.closest('.address-input-container');
      if (showSuggestions && !addressInputContainer) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showSuggestions]);

  const polygon = form.watch('polygon');

  // Initialize zoom based on polygon when it changes initially
  useEffect(() => {
    if (polygon && polygon.length > 0 && mapZoom === 6) {
      setMapZoom(14);
    }
  }, [polygon, mapZoom]);

  // Calculate area automatically when polygon changes
  useEffect(() => {
    if (polygon && polygon.length >= 3) {
      const area = calculatePolygonArea(polygon);
      if (area > 0) {
        form.setValue('area', Math.round(area).toString(), { shouldDirty: true, shouldValidate: false });
      }
    } else if (polygon && polygon.length < 3) {
      // Clear area if polygon is incomplete
      form.setValue('area', '', { shouldDirty: true, shouldValidate: false });
    }
  }, [polygon, form]);

  const saveMutation = useSaveEpaysageBase({
    onSuccess: (response) => {
      toast.success(mode === 'CREATE' ? 'Épaysage créé avec succès' : 'Modifications enregistrées avec succès');
      handleSuccess(response);
      if (mode === 'CREATE') {
        form.reset(buildDefaultValues(undefined, undefined));
      }
    },
    onError: (error) => {
      const message = getErrorMessage(error);
      setGeneralError(message);
      toast.error(message);
    },
  });

  const isSubmitting = saveMutation.isPending;

  const handleSuccess = (response: InventaireResponse) => {
    const inventaire = response.data;
    const finalInventaireId = inventaire.id;
    const finalEpaysageId = inventaire.epaysage_id ?? inventaire.epaysage?.id ?? epaysageId;

    setGeneralError(null);

    if (finalEpaysageId) {
      onSuccess?.({
        inventaireId: finalInventaireId,
        epaysageId: finalEpaysageId,
      });
    }
  };

  const handleSubmitForm = (values: EpaysageBasicFormValues) => {
    setGeneralError(null);
    const payload = mapFormToPayload(values, { mode, epaysageId, inventaireId });
    saveMutation.mutate(payload);
  };

  const handleMapClick = useCallback((coords: MapCoordinates) => {
    const nextPolygon = [...(polygon || []), coords];
    form.setValue('polygon', nextPolygon, { shouldDirty: true, shouldValidate: true });

    // If this is the first point, perform reverse geocoding to fill address, ville, and pays
    if (!polygon || polygon.length === 0) {
      setSelectedAddressCenter(null);

      // Perform reverse geocoding to get address, ville, and pays
      if (typeof window !== 'undefined' && window.google) {
        const geocoder = new window.google.maps.Geocoder();
        // @ts-ignore - Google Maps API types
        geocoder.geocode({ location: { lat: coords.lat, lng: coords.lng } }, (results: any, status: any) => {
          if (status === 'OK' && results && results[0]) {
            const result = results[0];

            // Extract formatted address
            const address = result.formatted_address || '';
            if (address) {
              form.setValue('address', address);
            }

            // Extract ville, pays, and zip_code from address components
            let ville = '';
            let pays = '';
            let zipCode = '';

            if (result.address_components) {
              result.address_components.forEach((component: any) => {
                const types = component.types;

                // Extract city/locality
                if (types.includes('locality') || types.includes('administrative_area_level_2')) {
                  ville = component.long_name;
                }

                // Extract country
                if (types.includes('country')) {
                  pays = component.long_name;
                }

                // Extract postal code
                if (types.includes('postal_code')) {
                  zipCode = component.long_name;
                }
              });
            }

            // Set ville, pays, and zip_code in the form
            if (ville) form.setValue('ville', ville);
            if (pays) form.setValue('pays', pays);
            if (zipCode) form.setValue('zip_code', zipCode);
          } else {
            // Log error but don't show to user (silent failure)
            console.warn('Reverse geocoding failed:', status);
          }
        });
      }
    }
  }, [polygon, form]);

  const handleMapMove = useCallback((center: MapCoordinates, zoom: number) => {
    setMapZoom(zoom);
  }, []);

  const handleRemoveLastPoint = () => {
    if (!polygon?.length) return;
    const nextPolygon = polygon.slice(0, -1);
    form.setValue('polygon', nextPolygon, { shouldDirty: true, shouldValidate: true });
  };

  const handleResetPolygon = () => {
    form.setValue('polygon', [], { shouldDirty: true, shouldValidate: true });
    // Keep selected address center when resetting polygon
  };

  const mapCenter = useMemo(() => {
    if (selectedAddressCenter) {
      return selectedAddressCenter;
    }
    if (polygon && polygon.length > 0) {
      return polygon[0]!;
    }
    return DEFAULT_CENTER;
  }, [polygon, selectedAddressCenter]);

  // Google Places Autocomplete
  const handleAddressSearch = useCallback(async (query: string) => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      // Check if window is available
      if (typeof window === 'undefined') {
        return;
      }

      // Initialize Google Places API if not already done
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.error('Google Maps API not loaded');
        return;
      }

      const service = new window.google.maps.places.AutocompleteService();
      service.getPlacePredictions(
        {
          input: query,
          types: ['address'],
          componentRestrictions: { country: 'fr' }, // France
        },
        // @ts-ignore - Google Maps API types
        (predictions: any, status: any) => {
          if (typeof window !== 'undefined' && status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setAddressSuggestions(predictions);
            setShowSuggestions(true);
          } else {
            setAddressSuggestions([]);
            setShowSuggestions(false);
          }
        }
      );
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  }, []);

  const handleAddressSelect = useCallback((placeId: string, description: string) => {
    // Check if window is available
    if (typeof window === 'undefined' || !window.google) {
      return;
    }

    // Set the address in the form
    form.setValue('address', description);

    // Get place details to extract coordinates and address components
    const geocoder = new window.google.maps.Geocoder();
    // @ts-ignore - Google Maps API types
    geocoder.geocode({ placeId }, (results: any, status: any) => {
      if (status === 'OK' && results && results[0]) {
        const result = results[0];

        // Extract coordinates from geometry.location
        if (result.geometry && result.geometry.location) {
          const location = result.geometry.location;
          const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
          const lng = typeof location.lng === 'function' ? location.lng() : location.lng;

          const coordinates: MapCoordinates = { lat, lng };
          setSelectedAddressCenter(coordinates);
          setMapZoom(14); // Zoom to street level when address is selected
        }

        // Extract ville, pays, and zip_code from address components
        let ville = '';
        let pays = '';
        let zipCode = '';

        if (result.address_components) {
          result.address_components.forEach((component: any) => {
            const types = component.types;

            // Extract city/locality
            if (types.includes('locality') || types.includes('administrative_area_level_2')) {
              ville = component.long_name;
            }

            // Extract country
            if (types.includes('country')) {
              pays = component.long_name;
            }

            // Extract postal code
            if (types.includes('postal_code')) {
              zipCode = component.long_name;
            }
          });
        }

        // Set ville, pays, and zip_code in the form
        if (ville) form.setValue('ville', ville);
        if (pays) form.setValue('pays', pays);
        if (zipCode) form.setValue('zip_code', zipCode);

        setShowSuggestions(false);
      }
    });
  }, [form]);

  const title = mode === 'CREATE' ? 'Créer un espace boisé' : 'Mettre à jour les informations';
  const subtitle =
    mode === 'CREATE'
      ? 'Enregistrez les informations essentielles et dessinez la zone directement sur la carte.'
      : 'Modifiez les informations générales et ajustez le polygone si nécessaire.';

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmitForm)} className="space-y-6">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <TreePine className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            </div>
          </div>
          {generalError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <div>
                <h4 className="font-medium">Erreur</h4>
                <p className="text-sm">{generalError}</p>
              </div>
            </Alert>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <MapPin className="h-5 w-5 text-primary" />
                  Localisation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Adresse *</FormLabel>
                      <FormControl>
                        <div className="address-input-container relative">
                          <Input
                            placeholder="Tapez une adresse..."
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                              handleAddressSearch(e.target.value);
                            }}
                            onFocus={() => {
                              if (addressSuggestions.length > 0) {
                                setShowSuggestions(true);
                              }
                            }}
                          />
                          {showSuggestions && addressSuggestions.length > 0 && (
                            <div className="address-suggestions-container absolute z-10 w-full mt-1 bg-card border border-border rounded-md shadow-lg max-h-60 overflow-auto">
                              {addressSuggestions.map((suggestion, index) => (
                                <div
                                  key={index}
                                  className="px-4 py-2 hover:bg-muted cursor-pointer border-b border-border last:border-b-0"
                                  onClick={() => {
                                    handleAddressSelect(suggestion.place_id, suggestion.description);
                                  }}
                                >
                                  <div className="text-sm font-medium">{suggestion.structured_formatting.main_text}</div>
                                  <div className="text-xs text-muted-foreground">{suggestion.structured_formatting.secondary_text}</div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="ville"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Département *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Département (auto-rempli)"
                            {...field}
                            className="bg-muted text-muted-foreground"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="pays"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Pays *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Pays (auto-rempli)"
                            {...field}
                            className="bg-muted text-muted-foreground"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="zip_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Code postal</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Code postal (auto-rempli)"
                          {...field}
                          className="bg-muted text-muted-foreground"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="area"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Ruler className="h-4 w-4" />
                        Superficie (m²)
                      </FormLabel>
                      <FormControl>
                        <Input type="number" placeholder="Ex: 3500" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <TreePine className="h-5 w-5 text-primary" />
                  Informations complémentaires
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="type_espace"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type d'espace *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner un type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TYPE_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {form.watch('type_espace') === 'autre' && (
                  <FormField
                    control={form.control}
                    name="type_espace_autre"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Précisez le type</FormLabel>
                        <FormControl>
                          <Input placeholder="Décrivez l'espace" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="code_site"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Code site</FormLabel>
                        <FormControl>
                          <Input placeholder="Code interne" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="num_sujet"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Identification</FormLabel>
                        <FormControl>
                          <Input placeholder="Nom / numéro" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="age_estime"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Âge estimé (années)</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder="Ex: 25" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            <div className="flex gap-3">
              {onCancel && (
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={onCancel}
                  disabled={isSubmitting}
                >
                  Annuler
                </Button>
              )}
              <Button type="submit" className="flex-1" disabled={isSubmitting}>
                {isSubmitting
                  ? mode === 'CREATE'
                    ? 'Création...'
                    : 'Mise à jour...'
                  : 'Enregistrer'}
              </Button>
            </div>
          </div>

          <Card className="h-full">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <MapPin className="h-5 w-5 text-primary" />
                Zone géographique
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="polygon"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="h-[450px] w-full rounded-xl overflow-hidden border-2 border-slate-200 shadow-inner">
                        <LeafletMap
                          options={{
                            center: mapCenter,
                            zoom: mapZoom,
                            scrollWheelZoom: true,
                            dragging: true,
                            zoomControl: true,

                          }}

                          key={mode === 'CREATE' ? 'create' : `update-${epaysageId}`}
                          eventHandlers={{
                            onMapClick: handleMapClick,
                            onMapMove: handleMapMove,
                          }}
                          polygons={
                            polygon && polygon.length >= 3
                              ? [
                                {
                                  id: 'epaysage-area',
                                  coordinates: polygon,

                                  fillColor: isFinished === false ? '#f87171' : '#22c55e',
                                  strokeColor: isFinished === false ? '#b91c1c' : '#15803d',
                                  fillOpacity: isFinished === false ? 0.25 : 0.2,
                                  strokeWeight: 2,
                                },
                              ]
                              : []
                          }
                          markers={
                            polygon
                              ? polygon.map((coord, index) => ({
                                id: `point-${index}`,
                                position: coord,
                                title: `Point ${index + 1}`,
                                data: {
                                  icon: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
                                },
                              }))
                              : []
                          }
                          style={{ height: '100%', width: '100%' }}
                        />
                      </div>
                    </FormControl>
                    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-primary/20 bg-primary/5 p-3">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Hash className="h-4 w-4 text-primary" />
                        {polygon?.length
                          ? `${polygon.length} point(s) définis`
                          : 'Cliquez sur la carte pour ajouter des points'}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleRemoveLastPoint}
                          disabled={!polygon?.length}
                        >
                          Supprimer le dernier
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleResetPolygon}
                          disabled={!polygon?.length}
                        >
                          Effacer tout
                        </Button>
                      </div>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <p className="text-xs text-muted-foreground">
                Astuce : cliquez sur la carte pour définir les sommets du polygone. Vous pouvez ajuster les points via les boutons ci-dessus.
              </p>
            </CardContent>
          </Card>
        </div>
      </form>
    </Form>
  );
}

function buildDefaultValues(
  initialValues?: EpaysageBasicFormInitialValues,
  initialPolygon?: MapCoordinates[]
): EpaysageBasicFormValues {
  return {
    address: initialValues?.address ?? '',
    ville: initialValues?.ville ?? '',
    pays: initialValues?.pays ?? '',
    zip_code: initialValues?.zip_code ?? '',
    area: initialValues?.area ?? '',
    type_espace: initialValues?.type_espace ?? '',
    type_espace_autre: initialValues?.type_espace_autre ?? '',
    code_site: initialValues?.code_site ?? '',
    num_sujet: initialValues?.num_sujet ?? '',
    age_estime: initialValues?.age_estime ?? '',
    polygon: initialPolygon ?? [],
  };
}

function mapFormToPayload(
  values: EpaysageBasicFormValues,
  options: { mode: 'CREATE' | 'UPDATE'; epaysageId?: number; inventaireId?: number }
): EpaysageBaseSaveInput {
  return {
    mode: options.mode,
    epaysage_id: options.epaysageId,
    inventaire_id: options.inventaireId,
    epaysage: {
      coord: values.polygon,
      address: values.address,
      ville: values.ville,
      pays: values.pays,
      area: values.area ? Number(values.area) : null,
      type_espace: values.type_espace,
      type_espace_autre:
        values.type_espace === 'autre' ? values.type_espace_autre || null : null,
      code_site: values.code_site || null,
      num_sujet: values.num_sujet || null,
      age_estime: values.age_estime ? Number(values.age_estime) : null,
    },
    inventaire: {
      is_finished: false,
      zip_code: values.zip_code || null,
    },
    ...(options.mode === 'CREATE' ? { creation_source: 'WEB' as const } : {}),
  };
}

/**
 * Calculate polygon area in square meters using equirectangular projection
 * This is an approximation that works well for small to medium polygons
 */
function calculatePolygonArea(coordinates: MapCoordinates[]): number {
  if (!coordinates || coordinates.length < 3) {
    return 0;
  }

  // Earth's radius in meters
  const earthRadius = 6378137; // meters
  const averageLat = coordinates.reduce((sum, point) => sum + point.lat, 0) / coordinates.length;
  const cosLat = Math.cos((averageLat * Math.PI) / 180);

  // Convert coordinates to meters using equirectangular projection
  const points = coordinates.map((point) => ({
    x: ((point.lng * Math.PI) / 180) * earthRadius * cosLat,
    y: ((point.lat * Math.PI) / 180) * earthRadius,
  }));

  // Ensure polygon is closed for area calculation
  const firstPoint = points[0];
  if (!firstPoint) {
    return 0;
  }
  const closedPoints = [...points, { ...firstPoint }];

  // Shoelace formula for polygon area
  let area = 0;
  for (let i = 0; i < closedPoints.length - 1; i++) {
    const current = closedPoints[i];
    const next = closedPoints[i + 1];
    if (!current || !next) {
      continue;
    }
    area += current.x * next.y;
    area -= next.x * current.y;
  }

  return Math.abs(area) / 2;
}

function getErrorMessage(error: unknown): string {
  if (!error) return 'Erreur lors de la sauvegarde';
  const maybeError = error as { response?: { data?: { message?: string } }; message?: string };
  return (
    maybeError.response?.data?.message ||
    maybeError.message ||
    'Erreur lors de la sauvegarde'
  );
}
